// Kauflands Bildvorgaben im Frontend.
//
// Die Bewertung kommt aus `vario-article-images` (`releasable`/`blockReason`), die Regel liegt
// im Paket `kaufland-product-data` (`pruefeKauflandBild`: JPEG, PNG, GIF, WebP, TIFF, hoechstens
// 10 MB). Hier steht nur, was Bilder-Reiter und Varianten-Grid gleich tun muessen: gesperrte
// Bilder erkennen, Altfreigaben von vor der Regel zuruecknehmen, den Hinweis formulieren.
// DOM-frei (Muster: lib/image-grid.ts).
//
// ANLASS (2026-09-17): Der Reiter zeigte jede `image/*`-Ressource aus dem DMS, also auch SVG
// oder BMP, und die Freigabe nahm sie an. Ein abgelehntes Bild fiel erst beim Push auf.

import type { GridState } from './image-grid.js';

/** Die Bewertungsfelder, wie `vario-article-images` sie liefert. Aeltere Antworten haben
 *  sie nicht; dann gilt das Bild als geeignet, die Function prueft beim Speichern ohnehin. */
export interface BewertetesBild {
  releasable?: boolean | undefined;
  blockReason?: string | null | undefined;
}

export function istFreigebbar(bild: BewertetesBild): boolean {
  return bild.releasable !== false;
}

/** Klartext fuer den Haendler; die Function liefert ihn, hier nur der Rueckfall. */
export function sperrgrund(bild: BewertetesBild): string {
  return typeof bild.blockReason === 'string' && bild.blockReason.length > 0
    ? bild.blockReason
    : 'Fuer Kaufland nicht geeignet.';
}

/**
 * Nimmt Freigaben gesperrter Bilder zurueck (Altbestand von vor der Regel, oder eine Datei,
 * die in Vario ersetzt wurde). Mutiert die Zeilen, damit der gemeinsame Zustand des Reiters
 * stimmt, und liefert die betroffenen IDs: Erst das Speichern loescht die Zeilen in der
 * Datenbank, deshalb muss der Aufrufer den Reiter als geaendert markieren.
 */
export function zieheGesperrteZurueck<T extends BewertetesBild & { resourceId: string; released: boolean }>(
  rows: T[],
): string[] {
  const raus: string[] = [];
  for (const r of rows) {
    if (r.released && !istFreigebbar(r)) {
      r.released = false;
      raus.push(r.resourceId);
    }
  }
  return raus;
}

/**
 * Entfernt gesperrte Bilder aus den genannten Spalten der Bild-Matrix (ohne `spalten`: aus
 * allen). Liefert einen NEUEN Zustand (der Startzustand bleibt unangetastet, sonst erkennt
 * `changedColumns` die Entfernung nicht) und die Zahl der entfernten Zuordnungen.
 *
 * Das Grid uebergibt nur die Kind-Spalten: Der Variantenvater ist bei Kaufland kein Produkt,
 * seine Spalte wird nie gespeichert. Wuerde man dort etwas entfernen, zeigte der Kopf eine
 * Aenderung an, die das Speichern nie loswird (Review-Fund 2026-09-17).
 */
export function entferneGesperrte(
  state: GridState,
  gesperrt: ReadonlySet<string>,
  spalten?: readonly string[],
): { state: GridState; entfernt: number } {
  const betroffen = spalten ? new Set(spalten) : null;
  const neu: GridState = {};
  let entfernt = 0;
  for (const [spalte, ids] of Object.entries(state)) {
    if (betroffen && !betroffen.has(spalte)) {
      neu[spalte] = [...ids];
      continue;
    }
    const bleibt = ids.filter((id) => !gesperrt.has(id));
    entfernt += ids.length - bleibt.length;
    neu[spalte] = bleibt;
  }
  return { state: neu, entfernt };
}

/** Der Hinweis ueber der Liste; null, wenn nichts gesperrt ist. */
export function sperrHinweis(gesperrt: number, zurueckgezogen: number): string | null {
  if (gesperrt <= 0) return null;
  const kopf = gesperrt === 1
    ? '1 Bild ist für Kaufland nicht geeignet (Format oder Größe) und lässt sich nicht freigeben.'
    : `${gesperrt} Bilder sind für Kaufland nicht geeignet (Format oder Größe) und lassen sich nicht freigeben.`;
  if (zurueckgezogen <= 0) return kopf;
  const schwanz = zurueckgezogen === 1
    ? '1 bestehende Freigabe davon wird beim Speichern entfernt.'
    : `${zurueckgezogen} bestehende Freigaben davon werden beim Speichern entfernt.`;
  return `${kopf} ${schwanz}`;
}
